/* A box you type code into, with the code coloured.
 *
 * The browser has no such control.  A `<textarea>` takes typing and gives you the caret, undo,
 * autoscroll and every key the platform defines, but it cannot colour a word of what it holds; a
 * `contenteditable` can be coloured and gets all of that wrong.  So the source is drawn **twice**:
 * a `<pre>` behind, carrying the colour, and the textarea in front with its own glyphs made
 * invisible and only its caret showing.
 *
 * Everything hard about that is one sentence: **the two layers must put every character in exactly
 * the same place**, or the caret sits somewhere other than the text it is in front of.  Sharing
 * the CSS is necessary and not sufficient, because the two do not lay out the *same* text — the
 * box has one uninterrupted run per line where the copy has one element per coloured run.  Three
 * things follow, and none of them is a nicety:
 *
 *   * the line height is a whole number of pixels (a fractional line box rounds differently in a
 *     textarea's internal layout than in a `<pre>`, a fifth of a pixel a line, a whole line out by
 *     the bottom of a long file);
 *   * kerning and ligatures are off (they form across a run, and the runs differ);
 *   * a coloured run may change the colour and **never the face** — an italic or a bold is a
 *     different font, whose advances the regular face makes no promise about, and the box has no
 *     spans to match it with.
 *
 * The first two live in `app.css` beside the shared metrics.  The third is a rule about every
 * class this ever renders, which is why it is written here as well as there.
 *
 * A fourth thing does not belong in a stylesheet at all: the copy is *moved* to follow the box's
 * scrolling and never scrolled itself — see `follow()`.
 *
 * None of that can be seen by a unit test, because it is a question about what a font and a
 * layout engine do.  `tools/overlay.mjs` drives a real browser and checks it: same metrics, the
 * colouring moves no glyph off where the plain text puts it, and the copy follows the box to both
 * ends of the file.
 *
 * This module knows nothing about Solvent, the sketch, or the shell: it is handed the text and a
 * function that says which runs of it are what.  That is what lets the harness build the very same
 * control the app builds, rather than a second one that might not have the bug.
 *
 * What an editor's features need of it is two things, and both are said here without saying what
 * any feature is: **marks in layers** (`setMarks(marks, layer)`), so the statement picked on the
 * drawing, the names under the caret and whatever comes next each keep their own and none has to
 * recompose the others'; and **the caret** (`onCaret`), told whenever it moves or the text under
 * it changes, so a feature that reads the word there is asked again exactly then. */

/** A run of the text and what it is.  `cls` becomes the element's class and the stylesheet says
 *  what it looks like — see the face rule above. */
/** A stretch of the text, by string index. */
export interface Extent { lo: number; hi: number }

export interface Run extends Extent { cls: string }

/** A stretch a caller has asked to be marked, and what to call it.  What it *means* is the
 *  caller's business; this class knows only that those characters carry one more class.
 *
 *  Marks may overlap — a statement can be both picked and judged — and a character under two of
 *  them carries both classes, so the stylesheet composes them rather than either one winning. */
export interface Mark extends Extent { cls: string }

/** Past this the colouring is dropped and the text is shown plain: one span per run stops being
 *  free long before a textarea's own limit, and a file this long is not one anybody is reading a
 *  word of.  Typing stays instant, which is the thing that must not be traded. */
const MAX_COLOUR = 200_000;

export class CodeEditor {
  /** What takes the typing.  Callers own the selection and the key handling through it; the
   *  colouring behind it is this class's business alone. */
  readonly box: HTMLTextAreaElement;
  private readonly copy: HTMLPreElement;
  private colour: (text: string) => Run[];
  /** The ranges callers have asked to be marked, by layer — each in no particular order, and
   *  possibly overlapping one another and the other layers' (`setMarks`).  An edit lets go of
   *  every layer but the default one, whose marks come with the text (`setText`): whatever the
   *  others said was said of offsets the edit has moved.
   *
   *  Marked with a class and **never with a weight**.  The box in front of this copy is one
   *  face throughout; a bold here would advance its glyphs differently from the box's, and
   *  every character after it on the line would sit beside the one the caret is on.  See the
   *  four rules in this file's header — this is the third of them.  A background, a box-shadow
   *  or an outline is safe, since none of the three takes any room; a border or a padding is
   *  the same mistake as a bold. */
  private layers = new Map<string, Mark[]>();
  /** Who is told the caret moved or the text changed (`onCaret`), and where it last was, so a
   *  move to where it already stood tells nobody. */
  private watchers: ((edited: boolean) => void)[] = [];
  private lastCaret = { lo: -1, hi: -1 };
  /** The text changed since the watchers were last told. */
  private dirty = false;
  /** The last colouring, against the text it was made from.  Colouring crosses the ABI and
   *  re-lexes the document, and a repaint that only moved the *mark* has the same text and so
   *  the same runs — which used to be paid for again anyway. */
  private lastRuns: { text: string; runs: Run[] } | null = null;
  /** Show the text uncoloured.  The colouring is the core's reading of Solvent, and prose read
   *  as statements — a README beside the sources — would be tinted at random. */
  plain = false;

  /** What colours the text — a program's scan or a drawing's, as the file shown is one or the
   *  other.  Changing it drops the last runs, which were the other language's reading. */
  set colouring(colour: (text: string) => Run[]) {
    if (colour === this.colour) return;
    this.colour = colour;
    this.lastRuns = null;
  }

  /** Build the two layers inside `host`, which is expected to be positioned (the stylesheet makes
   *  `#pcode` so).  The copy goes first so the box paints over it and takes the clicks. */
  constructor(host: HTMLElement, colour: (text: string) => Run[]) {
    this.colour = colour;
    this.copy = document.createElement('pre');
    this.copy.className = 'code-copy';
    this.copy.setAttribute('aria-hidden', 'true');
    this.box = document.createElement('textarea');
    this.box.className = 'code-box';
    for (const [k, v] of Object.entries({
      spellcheck: 'false', autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off',
      wrap: 'off',
    })) {
      this.box.setAttribute(k, v);
    }
    host.replaceChildren(this.copy, this.box);
    // the copy has no scrollbars of its own — it is moved by the box, which is the only one that
    // knows where the text has got to
    this.box.addEventListener('scroll', () => this.follow());
    // The caret.  A textarea says it moved in no one event: `selectionchange` on the document is
    // the general answer, where the browser sends it for a textarea's own selection, and the
    // rest cover one that does not — a key, a press, a word double-clicked.  `caretMoved`
    // tells each move once however many of them report it.
    this.box.addEventListener('input', () => { this.edited(); this.caretMoved(); });
    for (const e of ['keyup', 'pointerup', 'select', 'focus']) {
      this.box.addEventListener(e, () => this.caretMoved());
    }
    document.addEventListener('selectionchange', () => {
      if (document.activeElement === this.box) this.caretMoved();
    });
  }

  /** Be told whenever the caret moves, or the text it is in changes — `edited` then, since every
   *  offset a caller holds into the old text is stale.  Typing reports both, once. */
  onCaret(watcher: (edited: boolean) => void): void {
    this.watchers.push(watcher);
  }

  /** The selection, as string indices: `lo === hi` is a caret with nothing selected. */
  get caret(): Extent {
    return { lo: this.box.selectionStart, hi: this.box.selectionEnd };
  }

  /** Put the caret (or a selection) here, as a click there would, and bring its line on screen
   *  — scrolled to the middle only where it was off it, so a step to a line in view moves
   *  nothing else. */
  select(lo: number, hi = lo): void {
    this.box.setSelectionRange(lo, hi);
    const line = this.lineOf(lo);
    const each = this.lineHeight || 1;
    const top = this.box.scrollTop / each, rows = this.box.clientHeight / each;
    if (line < top || line >= top + rows - 1) this.scrollToLine(line, Math.floor(rows / 2));
    this.caretMoved();
  }

  /** Which line (from 0) a string index falls on.  Counted rather than sliced: `slice(0, off)
   *  .split` builds the whole prefix and an array of every line in it. */
  lineOf(off: number): number {
    const text = this.text;
    let line = 0;
    for (let i = 0; i < off && i < text.length; i += 1) if (text[i] === '\n') line += 1;
    return line;
  }

  /** The string index a point on the page (client pixels) falls nearest, as a click there would
   *  put the caret — for a press the box does not place itself, a right-click's.  Read off the
   *  copy, whose every character sits where the box's does (this file's header): the line by
   *  the whole-pixel line height, the column by where the copy draws the characters on it. */
  offsetAt(x: number, y: number): number {
    const text = this.text;
    const top = this.origin()[1];
    const last = this.lineOf(text.length);
    const line = Math.max(0, Math.min(last, Math.floor((y - top) / (this.lineHeight || 1))));
    let lo = 0;
    for (let l = 0; l < line; l += 1) lo = text.indexOf('\n', lo) + 1;
    const end = text.indexOf('\n', lo);
    let hi = end < 0 ? text.length : end;
    // the first character on the line whose middle is past the point: the caret goes before it
    const rect = this.rects(lo, hi);
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const r = rect(mid);
      if ((r.left + r.right) / 2 <= x) lo = mid + 1; else hi = mid;
    }
    return lo;
  }

  /** Where on the page (client pixels) the character at string index `off` starts: the left and
   *  the bottom of its line, where a menu opened at the caret goes. */
  pointAt(off: number): [number, number] {
    const text = this.text;
    const [left, top] = this.origin();
    const y = top + (this.lineOf(off) + 1) * this.lineHeight;
    if (off < text.length && text[off] !== '\n') return [this.rects(off, off + 1)(off).left, y];
    if (off > 0 && text[off - 1] !== '\n') return [this.rects(off - 1, off)(off - 1).right, y];
    return [left, y];
  }

  /** Where the text's first character sits on the page (client pixels): inside the box's border
   *  and padding, less how far it is scrolled. */
  private origin(): [number, number] {
    const box = this.box;
    const r = box.getBoundingClientRect();
    const style = getComputedStyle(box);
    return [r.left + box.clientLeft + (parseFloat(style.paddingLeft) || 0) - box.scrollLeft,
            r.top + box.clientTop + (parseFloat(style.paddingTop) || 0) - box.scrollTop];
  }

  /** Where the copy draws each character of `lo`..`hi`, as a function of its string index: the
   *  copy's text nodes over that stretch found once, so a search along a line walks it once. */
  private rects(lo: number, hi: number): (off: number) => DOMRect {
    const nodes: [Text, number][] = [];
    const walk = document.createTreeWalker(this.copy, NodeFilter.SHOW_TEXT);
    let at = 0;
    for (let n = walk.nextNode() as Text | null; n && at < hi; n = walk.nextNode() as Text | null) {
      if (at + n.length > lo) nodes.push([n, at]);
      at += n.length;
    }
    const range = document.createRange();
    return (off) => {
      let k = nodes.length - 1;
      while (k > 0 && nodes[k][1] > off) k -= 1;
      const [n, start] = nodes[k];
      range.setStart(n, off - start);
      range.setEnd(n, off - start + 1);
      return range.getBoundingClientRect();
    };
  }

  private caretMoved(): void {
    const { selectionStart: lo, selectionEnd: hi } = this.box;
    const edited = this.dirty;
    if (!edited && this.lastCaret.lo === lo && this.lastCaret.hi === hi) return;
    this.lastCaret = { lo, hi };
    this.dirty = false;
    for (const w of this.watchers) w(edited);
  }

  /** The text changed: the layers but the default are let go (`layers`), unpainted — the change
   *  is painted by whoever made it, `setText` or the typing's own repaint. */
  private edited(): void {
    this.dirty = true;
    for (const layer of [...this.layers.keys()]) if (layer !== '') this.layers.delete(layer);
  }

  get text(): string {
    return this.box.value;
  }

  /** Put text in, colour it, and leave the caret and the scroll where they were.  The caller
   *  decides whether that is the right thing; this only promises not to move them itself.
   *
   *  The marks come with the text because they usually move with it: a splice shifts every offset
   *  after it, so setting the two apart would repaint once against the old marks and again
   *  against the new ones. */
  setText(text: string, marks?: Mark[]): void {
    if (marks !== undefined) this.layers.set('', marks);
    const { selectionStart, selectionEnd, scrollTop, scrollLeft } = this.box;
    const changed = text !== this.box.value;
    this.box.value = text;
    this.box.setSelectionRange(selectionStart, selectionEnd);
    this.box.scrollTop = scrollTop;
    this.box.scrollLeft = scrollLeft;
    if (changed) this.edited();
    this.repaint();
    if (changed) this.caretMoved();
  }

  /** Mark these ranges in `layer` — the default one is the caller's who set the text — and no
   *  others there; every other layer's stay.  Repaints only when the layer actually changed, so a
   *  gesture that keeps landing on the same statement costs one paint and not sixty. */
  setMarks(marks: Mark[], layer = ''): void {
    const a = this.layers.get(layer) ?? [];
    if (a.length === marks.length
        && a.every((m, i) => m.lo === marks[i].lo && m.hi === marks[i].hi
                          && m.cls === marks[i].cls)) {
      return;
    }
    this.layers.set(layer, marks);
    this.repaint();
  }

  /** Colour what is in the box now — after typing, or after the rules changed. */
  repaint(): void {
    const text = this.text;
    const out = document.createDocumentFragment();
    // every layer's marks at once, by where they start: the stretches below are written in order,
    // so a mark is taken up when the text reaches it and let go once it is past — a repaint walks
    // the marks once, not once per coloured run
    const marks = [...this.layers.values()].flat().sort((a, b) => a.lo - b.lo);
    let taken = 0;
    let open: Mark[] = [];
    /** One piece of one colour: a bare string where it has nothing to say, a span where it has. */
    const piece = (a: number, b: number, cls: string): void => {
      if (b <= a) return;
      if (!cls) {
        out.append(text.slice(a, b));
        return;
      }
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = text.slice(a, b);
      out.append(span);
    };
    /* One stretch of text, cut wherever a mark starts or ends inside it, so a mark covers the
     * punctuation *between* coloured runs too — a statement is spans and gaps, and half a marked
     * statement would look like a colouring bug.  Cutting at every boundary rather than at one
     * mark's is what lets marks overlap: each piece carries the classes of every mark over it,
     * so a statement that is both picked and judged says both. */
    const put = (from: number, to: number, cls: string): void => {
      // an empty or inverted stretch contributes nothing.  Worth its own line: the tiling below
      // sorts its edges, so an inverted one would come back the right way round and emit the text
      // between them — characters appearing in the copy that the box does not have, which is the
      // one bug this whole class is built to avoid.
      if (to <= from) return;
      while (taken < marks.length && marks[taken].lo < to) open.push(marks[taken++]);
      if (open.length) open = open.filter((m) => m.hi > from);
      const cut = (i: number): number => Math.min(Math.max(i, from), to);
      const edges = [from, to];
      for (const m of open) edges.push(cut(m.lo), cut(m.hi));
      edges.sort((a, b) => a - b);
      for (let i = 0; i + 1 < edges.length; i += 1) {
        const [a, b] = [edges[i], edges[i + 1]];
        if (b <= a) continue;
        const over = open.filter((m) => m.lo <= a && m.hi >= b).map((m) => m.cls);
        piece(a, b, [cls, ...over].filter(Boolean).join(' '));
      }
    };
    let at = 0;
    if (!this.plain && text.length <= MAX_COLOUR) {
      for (const r of this.runs(text)) {
        put(at, r.lo, '');
        put(r.lo, r.hi, r.cls);
        at = r.hi;
      }
    }
    // the tail, and a newline past it: a `pre` drops the last one, and the copy has to be exactly
    // as tall as the box in front of it or the two scroll apart at the bottom
    put(at, text.length, '');
    out.append('\n');
    this.copy.replaceChildren(out);
    this.follow();
  }

  /** The colouring of this text, from last time where the text has not changed since. */
  private runs(text: string): Run[] {
    if (this.lastRuns?.text !== text) this.lastRuns = { text, runs: this.colour(text) };
    return this.lastRuns.runs;
  }

  /** One line, in pixels, as the stylesheet has it — asked for rather than written down, so a
   *  caller scrolling by lines cannot disagree with the layer that draws them. */
  get lineHeight(): number {
    return parseFloat(getComputedStyle(this.box).lineHeight) || 0;
  }

  /** Put `line` a few lines below the top, without taking the focus from wherever it is. */
  scrollToLine(line: number, margin = 4): void {
    this.box.scrollTop = Math.max(0, (line - margin) * this.lineHeight);
    this.follow();
  }

  /** Bring the copy to where the box is scrolled to.
   *
   *  By *moving* it, not by scrolling it.  Scrolling the copy looks like the obvious thing and is
   *  the bug this class exists to not have: the box carries scrollbars and the copy does not, so
   *  the box's client area is shorter by a scrollbar's width and its scroll range is longer by the
   *  same — and `copy.scrollTop = box.scrollTop` then *clamps* at the bottom of a long file,
   *  leaving the text a scrollbar's width above the caret sitting in it.  A translation has no
   *  range to clamp against, so the two cannot come apart however far down the file they are. */
  private follow(): void {
    this.copy.style.transform = `translate(${-this.box.scrollLeft}px, ${-this.box.scrollTop}px)`;
  }
}
